// src/pages/partner-admin/PartnerTeamPage.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  getPartnerTeam,
  updateTeamMemberRole,
  removeTeamMember,
  searchTeamUsers,
  addTeamMember,
} from "@/services/partnerAdminService";
import { usePartnerAdmin } from "@/contexts/PartnerAdminContext";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ROLES = ["admin", "manager", "judge", "viewer"];
const INVITE_ROLES = ["manager", "judge", "viewer"];

const ROLE_CONFIG = {
  admin:   { color: "#c8f04a", bg: "#1a2a0d", border: "#2a3a1a", label: "Admin" },
  manager: { color: "#60a5fa", bg: "#0d1a2a", border: "#1a2a3a", label: "Manager" },
  judge:   { color: "#f0a84a", bg: "#2a1f0d", border: "#3a2a1a", label: "Judge" },
  viewer:  { color: "#888",    bg: "#1a1a1a", border: "#2a2a2a", label: "Viewer" },
};

function RoleBadge({ role }) {
  const cfg = ROLE_CONFIG[role] || ROLE_CONFIG.viewer;
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, textTransform: "uppercase",
      letterSpacing: "0.07em", padding: "3px 8px", borderRadius: 4,
      color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`,
    }}>
      {cfg.label}
    </span>
  );
}

function Avatar({ profile, username, size = 34 }) {
  const src = profile?.avatar_url || profile?.avatar;
  const initials = (profile?.name || username || "?")
    .split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();

  if (src) {
    return (
      <img src={src} alt={username} style={{
        width: size, height: size, borderRadius: "50%",
        objectFit: "cover", flexShrink: 0,
      }} onError={e => { e.target.style.display = "none"; }} />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: "#2a2a2a", display: "flex", alignItems: "center",
      justifyContent: "center", fontSize: size * 0.35, color: "#888",
      fontWeight: 600, flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

// ─── Role selector ────────────────────────────────────────────────────────────

function RoleSelect({ memberId, currentRole, currentUserId, membUserId, onRoleChange }) {
  const [saving, setSaving] = useState(false);
  const isSelf = currentUserId === membUserId;

  const handleChange = async (e) => {
    const newRole = e.target.value;
    if (newRole === currentRole) return;
    setSaving(true);
    try { await onRoleChange(memberId, newRole); }
    finally { setSaving(false); }
  };

  if (isSelf) return <RoleBadge role={currentRole} />;

  return (
    <select value={currentRole} onChange={handleChange} disabled={saving} style={{
      background: "#1a1a1a", border: "1px solid #2a2a2a", color: "#aaa",
      padding: "4px 8px", borderRadius: 4, fontSize: 11,
      cursor: "pointer", outline: "none", opacity: saving ? 0.5 : 1,
    }}>
      {ROLES.map(r => <option key={r} value={r}>{ROLE_CONFIG[r].label}</option>)}
    </select>
  );
}

// ─── Add Member Modal ─────────────────────────────────────────────────────────

function AddMemberModal({ onClose, onAdded }) {
  const [query, setQuery]         = useState("");
  const [results, setResults]     = useState([]);
  const [selected, setSelected]   = useState(null);
  const [role, setRole]           = useState("viewer");
  const [searching, setSearching] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);
  const debounceRef = useRef(null);

  const handleSearch = (val) => {
    setQuery(val);
    setSelected(null);
    setError(null);
    clearTimeout(debounceRef.current);
    if (val.trim().length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await searchTeamUsers(val.trim());
        setResults(data);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const handleAdd = async () => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      const membership = await addTeamMember(selected.id, role);
      onAdded(membership);
      onClose();
    } catch (err) {
      setError(err.message || "Failed to add member.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pa-modal-overlay" onClick={onClose}>
      <div className="pa-modal" onClick={e => e.stopPropagation()}>
        <div className="pa-modal__header">
          <span className="pa-modal__title">Add Team Member</span>
          <button className="pa-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="pa-modal__body">
          <div className="pa-modal__label">Search by username or email</div>
          <input
            className="pa-modal__input"
            placeholder="e.g. johndoe or john@example.com"
            value={query}
            onChange={e => handleSearch(e.target.value)}
            autoFocus
          />

          {searching && <div className="pa-modal__hint">Searching...</div>}
          {!searching && query.length >= 2 && results.length === 0 && !selected && (
            <div className="pa-modal__hint">No users found.</div>
          )}
          {results.length > 0 && !selected && (
            <div className="pa-modal__results">
              {results.map(u => (
                <div
                  key={u.id}
                  className="pa-modal__result-row"
                  onClick={() => { setSelected(u); setResults([]); setQuery(u.username); }}
                >
                  <Avatar profile={{ name: u.name, avatar_url: u.avatar_url }} username={u.username} size={28} />
                  <div>
                    <div style={{ fontSize: 13, color: "#ccc" }}>{u.name || u.username}</div>
                    <div style={{ fontSize: 11, color: "#555" }}>@{u.username} · {u.email}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {selected && (
            <div className="pa-modal__selected">
              <Avatar profile={{ name: selected.name, avatar_url: selected.avatar_url }} username={selected.username} size={32} />
              <div>
                <div style={{ fontSize: 13, color: "#ccc", fontWeight: 500 }}>{selected.name || selected.username}</div>
                <div style={{ fontSize: 11, color: "#555" }}>@{selected.username}</div>
              </div>
              <button
                onClick={() => { setSelected(null); setQuery(""); }}
                style={{ marginLeft: "auto", background: "none", border: "none", color: "#555", cursor: "pointer", fontSize: 14 }}
              >✕</button>
            </div>
          )}

          <div className="pa-modal__label" style={{ marginTop: 16 }}>Role</div>
          <div className="pa-modal__role-grid">
            {INVITE_ROLES.map(r => {
              const cfg = ROLE_CONFIG[r];
              const active = role === r;
              return (
                <button
                  key={r}
                  onClick={() => setRole(r)}
                  className="pa-modal__role-btn"
                  style={{
                    borderColor: active ? cfg.color : "#222",
                    background: active ? cfg.bg : "#111",
                    color: active ? cfg.color : "#555",
                  }}
                >
                  <span style={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.07em" }}>{cfg.label}</span>
                </button>
              );
            })}
          </div>

          {error && <div className="pa-modal__error">{error}</div>}
        </div>

        <div className="pa-modal__footer">
          <button className="pa-modal__cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="pa-modal__confirm"
            onClick={handleAdd}
            disabled={!selected || saving}
          >
            {saving ? "Adding..." : "Add Member"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PartnerTeamPage() {
  const { membership } = usePartnerAdmin();
  const isAdmin = membership?.is_admin;

  const [members, setMembers]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [removing, setRemoving]   = useState(null);
  const [showModal, setShowModal] = useState(false);

  const currentUserId = (() => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) return null;
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.user_id || payload.id || null;
    } catch { return null; }
  })();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getPartnerTeam();
      setMembers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRoleChange = async (memberId, newRole) => {
    try {
      await updateTeamMemberRole(memberId, newRole);
      setMembers(ms => ms.map(m => m.id === memberId ? { ...m, role: newRole } : m));
    } catch (err) {
      alert(err.message || "Failed to update role.");
    }
  };

  const handleRemove = async (member) => {
    if (!confirm(`Remove ${member.user?.username} from the team?`)) return;
    setRemoving(member.id);
    try {
      await removeTeamMember(member.id);
      setMembers(ms => ms.filter(m => m.id !== member.id));
    } catch (err) {
      alert(err.message || "Failed to remove member.");
    } finally {
      setRemoving(null);
    }
  };

  const handleAdded = (newMembership) => {
    setMembers(ms => [...ms, newMembership]);
  };

  const grouped = ROLES.reduce((acc, role) => {
    acc[role] = members.filter(m => m.role === role);
    return acc;
  }, {});

  return (
    <div className="pa-team">
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
        {isAdmin && (
          <button className="pa-add-btn" onClick={() => setShowModal(true)}>
            + Add Member
          </button>
        )}
      </div>

      {loading ? (
        <div className="pa-page-loading">Loading team...</div>
      ) : error ? (
        <div className="pa-page-error">{error}</div>
      ) : members.length === 0 ? (
        <div className="pa-empty">No team members yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {ROLES.map(role => {
            const group = grouped[role];
            if (group.length === 0) return null;
            const cfg = ROLE_CONFIG[role];
            return (
              <div key={role}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: cfg.color }}>
                    {cfg.label}s
                  </span>
                  <span style={{ fontSize: 10, color: "#333", background: "#1a1a1a", border: "1px solid #222", borderRadius: 10, padding: "1px 7px" }}>
                    {group.length}
                  </span>
                </div>

                <div className="pa-table-wrap">
                  <table className="pa-table">
                    <thead>
                      <tr>
                        <th>Member</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Invited by</th>
                        <th>Joined</th>
                        {isAdmin && <th></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {group.map(member => {
                        const user = member.user || {};
                        const isSelf = currentUserId === user.id;
                        return (
                          <tr key={member.id} className="pa-table__row" style={{ cursor: "default" }}>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <Avatar profile={user.profile} username={user.username} />
                                <div>
                                  <div className="pa-table__title">
                                    {user.profile?.name || user.username}
                                    {isSelf && (
                                      <span style={{ marginLeft: 6, fontSize: 9, color: "#555", background: "#1e1e1e", border: "1px solid #2a2a2a", borderRadius: 3, padding: "1px 5px", textTransform: "uppercase", letterSpacing: "0.05em" }}>You</span>
                                    )}
                                  </div>
                                  <div className="pa-table__sub">@{user.username}</div>
                                </div>
                              </div>
                            </td>
                            <td className="pa-table__sub" style={{ color: "#555" }}>{user.email || "—"}</td>
                            <td>
                              {isAdmin ? (
                                <RoleSelect
                                  memberId={member.id}
                                  currentRole={member.role}
                                  currentUserId={currentUserId}
                                  membUserId={user.id}
                                  onRoleChange={handleRoleChange}
                                />
                              ) : (
                                <RoleBadge role={member.role} />
                              )}
                            </td>
                            <td className="pa-table__sub" style={{ color: "#555" }}>
                              {member.invited_by_username ? `@${member.invited_by_username}` : "—"}
                            </td>
                            <td className="pa-table__date">
                              {member.invited_at ? new Date(member.invited_at).toLocaleDateString() : "—"}
                            </td>
                            {isAdmin && (
                              <td>
                                {!isSelf && (
                                  <button
                                    className="pa-act-btn pa-act-btn--reject"
                                    onClick={() => handleRemove(member)}
                                    disabled={removing === member.id}
                                    style={{ width: "auto", padding: "4px 10px", fontSize: 11 }}
                                  >
                                    {removing === member.id ? "..." : "Remove"}
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <AddMemberModal
          onClose={() => setShowModal(false)}
          onAdded={handleAdded}
        />
      )}

      <style>{`
        .pa-team { display: flex; flex-direction: column; gap: 20px; }

        .pa-add-btn { background: #1a2a0d; border: 1px solid #2a3a1a; color: #c8f04a; padding: 7px 14px; border-radius: 6px; font-size: 12px; font-weight: 600; cursor: pointer; transition: background 0.1s; font-family: inherit; }
        .pa-add-btn:hover { background: #223a10; }

        .pa-table-wrap { background: #111; border: 1px solid #1e1e1e; border-radius: 8px; overflow: hidden; }
        .pa-table { width: 100%; border-collapse: collapse; }
        .pa-table thead tr { border-bottom: 1px solid #1e1e1e; }
        .pa-table th { text-align: left; padding: 10px 16px; font-size: 10px; color: #444; text-transform: uppercase; letter-spacing: 0.07em; font-weight: 600; }
        .pa-table__row { border-bottom: 1px solid #161616; transition: background 0.1s; }
        .pa-table__row:last-child { border-bottom: none; }
        .pa-table__row:hover { background: #161616; }
        .pa-table td { padding: 12px 16px; vertical-align: middle; }
        .pa-table__title { font-size: 13px; color: #ccc; font-weight: 500; }
        .pa-table__sub { font-size: 11px; color: #555; margin-top: 2px; }
        .pa-table__date { font-size: 11px; color: #555; white-space: nowrap; }

        .pa-act-btn { border-radius: 4px; border: none; cursor: pointer; font-weight: 600; display: inline-flex; align-items: center; justify-content: center; transition: background 0.1s; font-family: inherit; }
        .pa-act-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .pa-act-btn--reject { background: #2a1111; color: #f04a4a; }
        .pa-act-btn--reject:hover:not(:disabled) { background: #3a1a1a; }

        .pa-empty { background: #111; border: 1px dashed #222; border-radius: 8px; padding: 40px; display: flex; align-items: center; justify-content: center; color: #444; font-size: 13px; }
        .pa-page-loading { color: #555; font-size: 13px; padding: 40px 0; }
        .pa-page-error { color: #f04a4a; font-size: 13px; padding: 40px 0; }

        .pa-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .pa-modal { background: #111; border: 1px solid #222; border-radius: 10px; width: 420px; max-width: 95vw; display: flex; flex-direction: column; }
        .pa-modal__header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid #1e1e1e; }
        .pa-modal__title { font-size: 14px; font-weight: 600; color: #ccc; }
        .pa-modal__close { background: none; border: none; color: #555; cursor: pointer; font-size: 16px; line-height: 1; padding: 0; }
        .pa-modal__close:hover { color: #aaa; }
        .pa-modal__body { padding: 20px; display: flex; flex-direction: column; gap: 6px; }
        .pa-modal__label { font-size: 10px; color: #555; text-transform: uppercase; letter-spacing: 0.07em; font-weight: 600; margin-bottom: 2px; }
        .pa-modal__input { background: #0d0d0d; border: 1px solid #222; color: #ccc; padding: 9px 12px; border-radius: 6px; font-size: 13px; outline: none; width: 100%; box-sizing: border-box; font-family: inherit; }
        .pa-modal__input:focus { border-color: #333; }
        .pa-modal__hint { font-size: 11px; color: #444; padding: 6px 0; }
        .pa-modal__results { background: #0d0d0d; border: 1px solid #222; border-radius: 6px; overflow: hidden; margin-top: 4px; }
        .pa-modal__result-row { display: flex; align-items: center; gap: 10px; padding: 10px 12px; cursor: pointer; transition: background 0.1s; }
        .pa-modal__result-row:hover { background: #161616; }
        .pa-modal__selected { display: flex; align-items: center; gap: 10px; background: #0d0d0d; border: 1px solid #222; border-radius: 6px; padding: 10px 12px; margin-top: 4px; }
        .pa-modal__role-grid { display: flex; gap: 8px; margin-top: 2px; }
        .pa-modal__role-btn { flex: 1; padding: 8px 4px; border-radius: 6px; border: 1px solid; cursor: pointer; transition: all 0.1s; font-family: inherit; }
        .pa-modal__error { font-size: 12px; color: #f04a4a; margin-top: 4px; }
        .pa-modal__footer { display: flex; justify-content: flex-end; gap: 8px; padding: 14px 20px; border-top: 1px solid #1e1e1e; }
        .pa-modal__cancel { background: #1a1a1a; border: 1px solid #222; color: #666; padding: 7px 14px; border-radius: 6px; font-size: 12px; cursor: pointer; font-family: inherit; }
        .pa-modal__cancel:hover { color: #aaa; }
        .pa-modal__confirm { background: #1a2a0d; border: 1px solid #2a3a1a; color: #c8f04a; padding: 7px 14px; border-radius: 6px; font-size: 12px; font-weight: 600; cursor: pointer; font-family: inherit; }
        .pa-modal__confirm:disabled { opacity: 0.4; cursor: not-allowed; }
        .pa-modal__confirm:hover:not(:disabled) { background: #223a10; }
      `}</style>
    </div>
  );
}